import React, { useState, useEffect, useMemo, useRef } from "react";
import { format } from "date-fns";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import Divider from "@material-ui/core/Divider";

import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import { getCountryOptions, formatCountryWithDDI } from "../../utils/countries";

const useStyles = makeStyles(theme => ({
	root: { display: "flex", flexWrap: "wrap" },
	btnWrapper: { position: "relative" },
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	sectionTitle: {
		fontWeight: 600,
		marginTop: 20,
		marginBottom: 8,
		color: theme.palette.primary.main,
	},
}));

const SEGMENTOS = [
	"Tecnologia da Informação",
	"Saúde e Medicina",
	"Educação",
	"Varejo e Comércio",
	"Indústria e Manufatura",
	"Construção Civil",
	"Agronegócio",
	"Financeiro e Bancário",
	"Seguros",
	"Logística e Transporte",
	"Alimentação e Restaurantes",
	"Moda e Vestuário",
	"Imobiliário",
	"Jurídico",
	"Marketing e Publicidade",
	"Consultoria Empresarial",
	"Turismo e Hotelaria",
	"Energia e Utilities",
	"Telecomunicações",
	"Entretenimento e Mídia",
	"Outros",
];

const formatDateForInput = (date) => {
	if (!date) return "";
	if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
	const d = new Date(date);
	if (isNaN(d.getTime())) return "";
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
	if (!dateString) return null;
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
	if (dateString.includes("T")) return dateString.split("T")[0];
	return dateString;
};

const ContactSchema = Yup.object().shape({
	name: Yup.string().min(2, "Muito curto!").max(250, "Muito longo!").required("Obrigatório"),
	number: Yup.string().min(8, "Muito curto!").max(50, "Muito longo!"),
	email: Yup.string().email("Email inválido"),
	birthDate: Yup.date()
		.nullable()
		.min(new Date("1900-01-01"), "Data inválida")
		.max(new Date(), "Não pode ser no futuro"),
	cnpj: Yup.string().max(18, "CNPJ inválido").nullable(),
	cep: Yup.string().max(9, "CEP inválido").nullable(),
	country: Yup.string().required("Obrigatório"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		country: "",
		email: "",
		disableBot: false,
		birthDate: "",
		telefoneTel: "",
		cep: "",
		endereco: "",
		cidade: "",
		estado: "",
		razaoSocial: "",
		cnpj: "",
		segmento: "",
		observacoes: "",
	};

	const [contact, setContact] = useState(initialState);
	const [disableBot, setDisableBot] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [cepLoading, setCepLoading] = useState(false);
	const countryOptions = useMemo(() => getCountryOptions(i18n.language), []);

	useEffect(() => {
		return () => { isMounted.current = false; };
	}, []);

	// Buscar usuários para o campo Atendente
	useEffect(() => {
		if (!open) return;
		const fetchUsers = async () => {
			try {
				const { data } = await api.get("/users/", { params: { searchParam } });
				setOptions(data.users);
			} catch (err) {
				toastError(err);
			}
		};
		const timer = setTimeout(fetchUsers, searchParam.length === 0 ? 0 : 500);
		return () => clearTimeout(timer);
	}, [searchParam, open]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prev => ({
					...prev,
					...initialValues,
					birthDate: formatDateForInput(initialValues.birthDate),
				}));
			}
			if (!contactId) return;
			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...initialState,
						...data,
						birthDate: formatDateForInput(data.birthDate),
					});
					setDisableBot(data.disableBot);
					if (data.contactWallets && data.contactWallets.length > 0) {
						const wallet = data.contactWallets[0].wallet;
						const queue = data.contactWallets[0].queue;
						setSelectedUser({ id: wallet.id, name: wallet.name });
						setSelectedQueue(queue.id);
						setQueues([{ id: queue.id, name: queue.name }]);
					} else {
						setSelectedUser(null);
						setSelectedQueue(null);
						setQueues([]);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};
		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setSelectedUser(null);
		setSelectedQueue(null);
		setQueues([]);
	};

	const handleCepBlur = async (cep, setFieldValue) => {
		const cleanCep = cep.replace(/\D/g, "");
		if (cleanCep.length !== 8) return;
		setCepLoading(true);
		try {
			const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
			const data = await res.json();
			if (!data.erro) {
				setFieldValue("endereco", data.logradouro || "");
				setFieldValue("cidade", data.localidade || "");
				setFieldValue("estado", data.uf || "");
			}
		} catch {
			// silencioso — usuário preenche manualmente se falhar
		} finally {
			setCepLoading(false);
		}
	};

	const handleSaveContact = async (values) => {
		try {
			const contactData = {
				...values,
				disableBot,
				birthDate: parseDateFromInput(values.birthDate),
			};

			if (contactId) {
				if (!selectedUser && !selectedQueue) {
					await api.delete(`/contacts/wallet/${contactId}`);
				}
				delete contactData.contactWallets;
				await api.put(`/contacts/${contactId}`, contactData);
				if (selectedUser && selectedQueue) {
					await api.put(`/contacts/wallet/${contactId}`, {
						wallets: { userId: selectedUser.id, queueId: selectedQueue },
					});
				}
				handleClose();
			} else {
				delete contactData.contactWallets;
				const { data } = await api.post("/contacts", contactData);
				if (data.id && selectedUser && selectedQueue) {
					await api.put(`/contacts/wallet/${data.id}`, {
						wallets: { userId: selectedUser.id, queueId: selectedQueue },
					});
				}
				if (onSave) onSave(data);
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
				<DialogTitle>
					{contactId ? i18n.t("contactModal.title.edit") : i18n.t("contactModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>

								{/* ── DADOS PESSOAIS ── */}
								<Typography className={classes.sectionTitle} variant="subtitle1">
									Dados Pessoais
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="Nome *"
											name="name"
											autoFocus
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="WhatsApp *"
											name="number"
											error={touched.number && Boolean(errors.number)}
											helperText={touched.number && errors.number}
											placeholder="5511999999999"
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Autocomplete
											value={countryOptions.find((o) => o.code === values.country) || null}
											onChange={(e, newValue) => setFieldValue("country", newValue?.code || "")}
											options={countryOptions}
											getOptionLabel={(option) => formatCountryWithDDI(option.code)}
											isOptionEqualToValue={(option, value) => option.code === value.code}
											renderInput={(params) => (
												<TextField
													{...params}
													label="País *"
													error={touched.country && Boolean(errors.country)}
													helperText={touched.country && errors.country}
													variant="outlined"
													size="small"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="Telefone"
											name="telefoneTel"
											placeholder="1133334444"
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="E-mail"
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="Data de Nascimento"
											name="birthDate"
											type="date"
											InputLabelProps={{ shrink: true }}
											inputProps={{ min: "1900-01-01", max: new Date().toISOString().split("T")[0] }}
											variant="outlined"
											size="small"
											fullWidth
											error={touched.birthDate && Boolean(errors.birthDate)}
											helperText={touched.birthDate && errors.birthDate}
											onChange={(e) => setFieldValue("birthDate", parseDateFromInput(e.target.value))}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<div style={{ marginTop: 4 }}>
											<TagsContainer contact={contact} />
										</div>
									</Grid>
								</Grid>

								<Divider style={{ margin: "16px 0" }} />

								{/* ── ENDEREÇO ── */}
								<Typography className={classes.sectionTitle} variant="subtitle1">
									Endereço
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={3}>
										<Field
											as={TextField}
											label="CEP"
											name="cep"
											placeholder="00000-000"
											variant="outlined"
											size="small"
											fullWidth
											InputProps={{ endAdornment: cepLoading ? <CircularProgress size={16} /> : null }}
											onBlur={(e) => handleCepBlur(e.target.value, setFieldValue)}
										/>
									</Grid>
									<Grid item xs={12} sm={9}>
										<Field
											as={TextField}
											label="Endereço"
											name="endereco"
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={8}>
										<Field
											as={TextField}
											label="Cidade"
											name="cidade"
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<Field
											as={TextField}
											label="Estado (UF)"
											name="estado"
											placeholder="SP"
											inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
								</Grid>

								<Divider style={{ margin: "16px 0" }} />

								{/* ── DADOS EMPRESARIAIS ── */}
								<Typography className={classes.sectionTitle} variant="subtitle1">
									Dados Empresariais
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="Razão Social"
											name="razaoSocial"
											variant="outlined"
											size="small"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label="CNPJ"
											name="cnpj"
											placeholder="00.000.000/0000-00"
											variant="outlined"
											size="small"
											fullWidth
											error={touched.cnpj && Boolean(errors.cnpj)}
											helperText={touched.cnpj && errors.cnpj}
										/>
									</Grid>
									<Grid item xs={12}>
										<FormControl variant="outlined" size="small" fullWidth>
											<InputLabel>Segmento</InputLabel>
											<Select
												value={values.segmento || ""}
												onChange={(e) => setFieldValue("segmento", e.target.value)}
												label="Segmento"
											>
												<MenuItem value=""><em>Selecione...</em></MenuItem>
												{SEGMENTOS.map((s) => (
													<MenuItem key={s} value={s}>{s}</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								</Grid>

								<Divider style={{ margin: "16px 0" }} />

								{/* ── ATENDIMENTO ── */}
								<Typography className={classes.sectionTitle} variant="subtitle1">
									Atendimento
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Autocomplete
											value={selectedUser}
											fullWidth
											getOptionLabel={(option) => option.name}
											onChange={(e, newValue) => {
												setSelectedUser(newValue);
												if (newValue && Array.isArray(newValue.queues)) {
													if (newValue.queues.length === 1) setSelectedQueue(newValue.queues[0].id);
													setQueues(newValue.queues);
												} else {
													setQueues(allQueues);
													setSelectedQueue(null);
													setSelectedUser(null);
												}
											}}
											options={options}
											filterOptions={(opts, { inputValue }) =>
												opts.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()))
											}
											noOptionsText="Nenhum atendente encontrado"
											loading={loading}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Atendente"
													variant="outlined"
													size="small"
													onChange={(e) => setSearchParam(e.target.value)}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<>
																{loading ? <CircularProgress color="inherit" size={16} /> : null}
																{params.InputProps.endAdornment}
															</>
														),
													}}
												/>
											)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormControl variant="outlined" size="small" fullWidth>
											<InputLabel>Setor</InputLabel>
											<Select
												value={selectedQueue || ""}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label="Setor"
											>
												{queues.map((q) => (
													<MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								</Grid>

								<Divider style={{ margin: "16px 0" }} />

								{/* ── OBSERVAÇÕES ── */}
								<Typography className={classes.sectionTitle} variant="subtitle1">
									Observações
								</Typography>
								<Field
									as={TextField}
									name="observacoes"
									multiline
									rows={4}
									variant="outlined"
									fullWidth
									placeholder="Informações adicionais sobre o contato..."
								/>

								<Divider style={{ margin: "16px 0" }} />

								{/* ── CONFIGURAÇÕES ── */}
								<Typography className={classes.sectionTitle} variant="subtitle1">
									Configurações
								</Typography>
								<Typography variant="body2" style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<Switch
										size="small"
										checked={disableBot}
										onChange={() => setDisableBot(!disableBot)}
										name="disableBot"
									/>
									{i18n.t("contactModal.form.chatBotContact")}
								</Typography>
								{contact?.whatsapp && (
									<Typography variant="body2" style={{ marginTop: 8, color: "#666" }}>
										{i18n.t("contactModal.form.whatsapp")}: {contact.whatsapp.name}
									</Typography>
								)}
								{contact?.lgpdAcceptedAt && (
									<Typography variant="body2" style={{ marginTop: 4, color: "#666" }}>
										{i18n.t("contactModal.form.termsLGDP")}: {format(new Date(contact.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm")}
									</Typography>
								)}

							</DialogContent>
							<DialogActions>
								<Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId ? i18n.t("contactModal.buttons.okEdit") : i18n.t("contactModal.buttons.okAdd")}
									{isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
